import { useState, useEffect } from 'react'
import { useAuth } from '@clerk/clerk-react'
import { useMutation, useQuery } from '@tanstack/react-query'
import { ChevronRight, ChevronLeft, X, Users, Calendar, Target, Utensils, Check, Sparkles } from 'lucide-react'
import { api } from '@/lib/api'
import { getWeekStartDate } from '@/lib/utils'
import type { UserProfile, WeeklyPlan } from '@/types/models'

const DIET_TYPES = [
  { id: 'balanced', label: 'Balanced' },
  { id: 'high_protein', label: 'High Protein' },
  { id: 'keto', label: 'Keto' },
  { id: 'low_carb', label: 'Low Carb' },
  { id: 'mediterranean', label: 'Mediterranean' },
  { id: 'vegan', label: 'Vegan' },
  { id: 'vegetarian', label: 'Vegetarian' },
  { id: 'paleo', label: 'Paleo' },
  { id: 'gluten_free', label: 'Gluten-Free' },
  { id: 'dairy_free', label: 'Dairy-Free' },
]

const GROUP_PRESETS = [
  { value: 1, label: 'Just Me', emoji: '🧑' },
  { value: 2, label: '2 People', emoji: '👫' },
  { value: 4, label: 'Family of 4', emoji: '👨‍👩‍👧‍👦' },
  { value: 6, label: '6 People', emoji: '🏘️' },
]

interface WizardData {
  week_start_date: string
  days_count: number
  default_meals_per_day: number
  default_group_size: number
  diet_types: string[]
  goal_mode: 'calories' | 'macros'
  calorie_target: number | null
  protein_target_g: number | null
  carbs_target_g: number | null
  fat_target_g: number | null
  fiber_target_g: number | null
}

const STEPS = [
  { label: 'Schedule', icon: Calendar },
  { label: 'Group Size', icon: Users },
  { label: 'Diet', icon: Utensils },
  { label: 'Goals', icon: Target },
]

interface Props {
  onComplete: (plan: WeeklyPlan) => void
  onCancel: () => void
}

export default function WeeklyWizard({ onComplete, onCancel }: Props) {
  const { getToken } = useAuth()
  const [step, setStep] = useState(0)
  const [initialized, setInitialized] = useState(false)

  // Load saved profile to pre-fill defaults
  const { data: profileRes } = useQuery({
    queryKey: ['user-profile'],
    queryFn: async () => {
      const token = await getToken()
      return api.get<{ data: { profile: UserProfile } }>('/users/me', token!)
    },
  })
  const profile = (profileRes as { data: { profile: UserProfile } } | undefined)?.data?.profile

  const [data, setData] = useState<WizardData>({
    week_start_date: getWeekStartDate(0),
    days_count: 7,
    default_meals_per_day: 3,
    default_group_size: 1,
    diet_types: [],
    goal_mode: 'calories',
    calorie_target: 2000,
    protein_target_g: null,
    carbs_target_g: null,
    fat_target_g: null,
    fiber_target_g: null,
  })

  // Once profile loads, seed the form with saved defaults (only once)
  useEffect(() => {
    if (profile && !initialized) {
      setData(d => ({
        ...d,
        days_count: profile.default_days ?? 7,
        default_meals_per_day: profile.default_meals_per_day ?? 3,
        default_group_size: profile.default_group_size ?? 1,
        diet_types: profile.diet_types ?? [],
        goal_mode: profile.goal_mode ?? 'calories',
        calorie_target: profile.calorie_target ?? 2000,
        protein_target_g: profile.protein_target_g ?? null,
        carbs_target_g: profile.carbs_target_g ?? null,
        fat_target_g: profile.fat_target_g ?? null,
        fiber_target_g: profile.fiber_target_g ?? null,
      }))
      setInitialized(true)
    }
  }, [profile, initialized])

  const createPlan = useMutation({
    mutationFn: async () => {
      const token = await getToken()
      const res = await api.post<{ data: WeeklyPlan }>('/plans', data, token!)
      return (res as { data: WeeklyPlan }).data
    },
    onSuccess: onComplete,
  })

  function toggleDiet(id: string) {
    setData((d) => ({
      ...d,
      diet_types: d.diet_types.includes(id)
        ? d.diet_types.filter((x) => x !== id)
        : [...d.diet_types, id],
    }))
  }

  const isLastStep = step === STEPS.length - 1

  // Week options: this week and next 3 weeks
  const weekOptions = Array.from({ length: 4 }, (_, i) => {
    const start = getWeekStartDate(i)
    const end = new Date(start + 'T00:00:00')
    end.setDate(end.getDate() + 6)
    return {
      value: start,
      label: i === 0 ? 'This week' : i === 1 ? 'Next week' : `In ${i} weeks`,
      range: `${new Date(start + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} – ${end.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`,
    }
  })

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 pt-6 pb-4 border-b border-gray-100">
          <div>
            <h2 className="text-lg font-bold text-gray-900">Plan a New Week</h2>
            <p className="text-sm text-gray-500">{STEPS[step].label}</p>
          </div>
          <button onClick={onCancel} className="p-2 hover:bg-gray-100 rounded-lg text-gray-400">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Step indicators */}
        <div className="flex px-6 pt-4 gap-2">
          {STEPS.map((s, i) => {
            const Icon = s.icon
            return (
              <div key={i} className="flex-1 flex flex-col items-center gap-1">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center transition-colors ${
                  i < step ? 'bg-brand-600 text-white' :
                  i === step ? 'bg-brand-600 text-white ring-4 ring-brand-100' :
                  'bg-gray-100 text-gray-400'
                }`}>
                  {i < step ? <Check className="w-4 h-4" /> : <Icon className="w-4 h-4" />}
                </div>
                <span className={`text-xs font-medium ${i === step ? 'text-brand-600' : 'text-gray-400'}`}>
                  {s.label}
                </span>
              </div>
            )
          })}
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-6 py-5">

          {/* Step 0 – Schedule */}
          {step === 0 && (
            <div className="space-y-5">
              <div>
                <label className="label">Which week are you planning?</label>
                <div className="space-y-2">
                  {weekOptions.map((w) => (
                    <button
                      key={w.value}
                      onClick={() => setData((d) => ({ ...d, week_start_date: w.value }))}
                      className={`w-full flex items-center justify-between px-4 py-3 rounded-xl border-2 text-left transition-colors ${
                        data.week_start_date === w.value
                          ? 'border-brand-600 bg-brand-50'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <span className={`font-medium text-sm ${data.week_start_date === w.value ? 'text-brand-700' : 'text-gray-700'}`}>
                        {w.label}
                      </span>
                      <span className="text-xs text-gray-400">{w.range}</span>
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="label">How many days?</label>
                <div className="flex items-center gap-4">
                  <input
                    type="range" min={1} max={7} value={data.days_count}
                    onChange={(e) => setData((d) => ({ ...d, days_count: +e.target.value }))}
                    className="flex-1 accent-brand-600"
                  />
                  <span className="w-20 text-center font-bold text-brand-600">
                    {data.days_count} {data.days_count === 1 ? 'day' : 'days'}
                  </span>
                </div>
              </div>

              <div>
                <label className="label">Meals per day (default)</label>
                <div className="flex gap-2">
                  {[2, 3, 4, 5].map((n) => (
                    <button key={n} onClick={() => setData((d) => ({ ...d, default_meals_per_day: n }))}
                      className={`flex-1 py-2.5 rounded-lg text-sm font-semibold border-2 transition-colors ${
                        data.default_meals_per_day === n
                          ? 'border-brand-600 bg-brand-50 text-brand-700'
                          : 'border-gray-200 text-gray-600 hover:border-gray-300'
                      }`}
                    >{n}</button>
                  ))}
                </div>
                <p className="text-xs text-gray-400 mt-1.5">Customize per day inside the Meal Builder</p>
              </div>
            </div>
          )}

          {/* Step 1 – Group Size */}
          {step === 1 && (
            <div className="space-y-5">
              <p className="text-sm text-gray-500">
                Sets ingredient quantities and shopping amounts. Override any time for a specific day or meal.
              </p>
              <div className="grid grid-cols-2 gap-3">
                {GROUP_PRESETS.map((p) => (
                  <button key={p.value} onClick={() => setData((d) => ({ ...d, default_group_size: p.value }))}
                    className={`flex flex-col items-center gap-1.5 py-4 rounded-xl border-2 text-sm font-medium transition-colors ${
                      data.default_group_size === p.value
                        ? 'border-brand-600 bg-brand-50 text-brand-700'
                        : 'border-gray-200 text-gray-600 hover:border-gray-300'
                    }`}
                  >
                    <span className="text-2xl">{p.emoji}</span>
                    {p.label}
                  </button>
                ))}
              </div>
              <div>
                <label className="label">Custom number (1–20)</label>
                <div className="flex items-center gap-3">
                  <input type="number" min={1} max={20}
                    value={data.default_group_size}
                    onChange={(e) => setData((d) => ({ ...d, default_group_size: Math.min(20, Math.max(1, +e.target.value)) }))}
                    className="input w-24"
                  />
                  <span className="text-sm text-gray-500">people</span>
                </div>
              </div>
            </div>
          )}

          {/* Step 2 – Diet */}
          {step === 2 && (
            <div className="space-y-4">
              <p className="text-sm text-gray-500">Select all that apply for this week's plan.</p>
              <div className="grid grid-cols-2 gap-2">
                {DIET_TYPES.map((d) => (
                  <button key={d.id} onClick={() => toggleDiet(d.id)}
                    className={`flex items-center gap-2 px-3 py-2.5 rounded-lg text-sm font-medium border-2 transition-colors ${
                      data.diet_types.includes(d.id)
                        ? 'border-brand-600 bg-brand-50 text-brand-700'
                        : 'border-gray-200 text-gray-600 hover:border-gray-300'
                    }`}
                  >
                    {data.diet_types.includes(d.id) && <Check className="w-3.5 h-3.5 flex-shrink-0" />}
                    {d.label}
                  </button>
                ))}
              </div>
              {data.diet_types.length === 0 && (
                <p className="text-xs text-gray-400 italic">No preferences = balanced suggestions</p>
              )}
            </div>
          )}

          {/* Step 3 – Nutritional Goals */}
          {step === 3 && (
            <div className="space-y-5">
              <div className="flex gap-3">
                {(['calories', 'macros'] as const).map((mode) => (
                  <button key={mode} onClick={() => setData((d) => ({ ...d, goal_mode: mode }))}
                    className={`flex-1 py-2.5 rounded-lg text-sm font-semibold border-2 transition-colors ${
                      data.goal_mode === mode
                        ? 'border-brand-600 bg-brand-50 text-brand-700'
                        : 'border-gray-200 text-gray-600 hover:border-gray-300'
                    }`}
                  >
                    {mode === 'calories' ? '🔢 Calorie Target' : '⚗️ Full Macros'}
                  </button>
                ))}
              </div>

              {data.goal_mode === 'calories' ? (
                <div>
                  <label className="label">Daily calorie target <span className="text-gray-400 font-normal">(per person)</span></label>
                  <div className="flex items-center gap-3">
                    <input type="number" min={500} max={5000} step={50}
                      value={data.calorie_target ?? 2000}
                      onChange={(e) => setData((d) => ({ ...d, calorie_target: +e.target.value }))}
                      className="input w-36"
                    />
                    <span className="text-sm text-gray-500">kcal / day</span>
                  </div>
                </div>
              ) : (
                <div className="grid grid-cols-2 gap-4">
                  {[
                    { key: 'protein_target_g', label: 'Protein (g)', placeholder: '150' },
                    { key: 'carbs_target_g', label: 'Carbs (g)', placeholder: '200' },
                    { key: 'fat_target_g', label: 'Fat (g)', placeholder: '65' },
                    { key: 'fiber_target_g', label: 'Fiber (g)', placeholder: '30' },
                  ].map(({ key, label, placeholder }) => (
                    <div key={key}>
                      <label className="label">{label}</label>
                      <input type="number" min={0} placeholder={placeholder}
                        value={(data[key as keyof WizardData] as number | null) ?? ''}
                        onChange={(e) => setData((d) => ({ ...d, [key]: +e.target.value || null }))}
                        className="input"
                      />
                    </div>
                  ))}
                  <p className="col-span-2 text-xs text-gray-400">Per person · per day</p>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-6 py-4 border-t border-gray-100">
          <button onClick={step === 0 ? onCancel : () => setStep(s => s - 1)}
            className="btn-secondary flex items-center gap-1.5"
          >
            {step === 0 ? <><X className="w-4 h-4" /> Cancel</> : <><ChevronLeft className="w-4 h-4" /> Back</>}
          </button>

          {isLastStep ? (
            <button
              onClick={() => createPlan.mutate()}
              disabled={createPlan.isPending}
              className="btn-primary flex items-center gap-2"
            >
              {createPlan.isPending ? (
                <><span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" /> Creating…</>
              ) : (
                <><Sparkles className="w-4 h-4" /> Start Planning</>
              )}
            </button>
          ) : (
            <button onClick={() => setStep(s => s + 1)} className="btn-primary flex items-center gap-1.5">
              Continue <ChevronRight className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>
    </div>
  )
}
